import axiosInstance from "services/common/axiosInstance";

export interface ProductBenefit {
  id: number;
  product_id: number;
  benfit_title: string;
  benfit_description: string;
  status: string;
  created_on: string;
  created_by: string;
  updated_on: string | null;
  updated_by: string | null;
}

export interface ProductAdditionalInfo {
  id: number;
  product_id: number;
  policy_term: string;
  premium_term: string;
  minimum_sum_assured: number;
  maximum_sum_assured: number;
  maturity_bonus_benefit: string;
  death_benefit: string;
  loan_facility: string;
  surrendar_value: string;
  status: string;
  created_on: string;
  created_by: string;
  updated_on: string | null;
  updated_by: string | null;
}

export interface ProductEligibility {
  id: number;
  product_id: number;
  min_age: number;
  max_age: number;
  customer_eligibility: string;
  medical_requirement: string;
  status: string;
  created_on: string;
  created_by: string;
  updated_on: string | null;
  updated_by: string | null;
}

export interface ProductPremium {
  id: number;
  product_id: number;
  premium_note: string;
  age: string;
  monthly_bd: number;
  quarterly_bd: number;
  half_yearly_bd: number;
  yearly_bd: number;
  sum_assured: number;
  status: string;
  created_on: string;
  created_by: string;
  updated_on: string | null;
  updated_by: string | null;
}

export interface Product {
  id: number;
  product_category: string;
  product_code: string;
  product_name: string;
  product_description: string;
  plan_type: string;
  product_image_url: string;
  status: string;
  created_on: string;
  created_by: string;
  updated_on: string | null;
  updated_by: string | null;

  product_benfit: ProductBenefit[];
  product_additional_info: ProductAdditionalInfo | {};
  product_eligibility: ProductEligibility[];
  product_premium: ProductPremium | {};
}

export interface Pagination {
  page: number;
  per_page: number;
  total_pages: number;
  total_items: number;
}

export interface AddProductParams {
  product_category: string;
  product_code: string;
  product_name: string;
  product_description: string;
  plan_type: string;
  product_image_url: string;
}

export interface AddProductBenefitParams {
  product_id: number;
  benfit_title: string;
  benfit_description: string;
}

export interface AddProductAdditionalInfoParams {
  product_id: number;
  policy_term: string;
  premium_term: string;
  minimum_sum_assured: number;
  maximum_sum_assured: number;
  maturity_bonus_benefit: string;
  death_benefit: string;
  loan_facility: string;
  surrendar_value: string;
}


export interface AddProductEligibilityParams {
  product_id: number;
  min_age: number;
  max_age: number;
  customer_eligibility: string;
  medical_requirement: string;
}

export interface AddProductPremiumParams {
  product_id: number;
  premium_note: string;
  age: string;
  monthly_bd: number;
  quarterly_bd: number;
  half_yearly_bd: number;
  yearly_bd: number;
  sum_assured: number;
}

export interface GetProductListParams {
  page: number;
  per_page: number;
  product_code?: string;
  product_category?: string;
  product_name?: string;
  searchkey: string;
}


const handleRequest = async (promise: Promise<any>) => {
  try {
    const response = await promise;
    return response.data;
  } catch (error: any) {
    throw error?.response?.data || error.message;
  }
};

const BASE_URL = "/product";

export const addNewProduct = (data: AddProductParams) =>
  handleRequest(axiosInstance.post(`${BASE_URL}/addnewproduct`, data));

export const addProductBenefit = (data: AddProductBenefitParams) =>
  handleRequest(axiosInstance.post(`${BASE_URL}/addproductbenfit`, data));

export const addProductAdditionalInfo = (data: AddProductAdditionalInfoParams) =>
  handleRequest(axiosInstance.post(`${BASE_URL}/addproductAddInfo`, data));

export const addProductEligibility = (data: AddProductEligibilityParams) =>
  handleRequest(axiosInstance.post(`${BASE_URL}/addproducteligibility`, data));

export const addProductPremium = (data: AddProductPremiumParams) =>
  handleRequest(axiosInstance.post(`${BASE_URL}/addproductpremium`, data));

export const updateProduct = (id: number, data: any) =>
  handleRequest(
    axiosInstance.put(`${BASE_URL}/updateproduct?id=${id}`, data)
  );


export const updateProductBenefit = (id: number, data: any) =>
  handleRequest(axiosInstance.put(`${BASE_URL}/updateproductbenfit?id=${id}`, data ));

export const updateProductAdditionalInfo = (id: number, data: any) =>
  handleRequest(axiosInstance.put(`${BASE_URL}/updateproductaddinfo?id=${id}`, data ));

export const updateProductEligibility = (id: number, data: any) =>
  handleRequest(axiosInstance.put(`${BASE_URL}/updateproducteligibility?id=${id}`, data ));

export const updateProductPremium = (id: number, data: any) =>
  handleRequest(axiosInstance.put(`${BASE_URL}/updateproductpremium?id=${id}`, data ));

export const deleteProductBenefit = (id: number) =>
  handleRequest(
    axiosInstance.delete(
      `${BASE_URL}/deleteproductbenfit`,
      { params: { id } }
    )
  );


export const deleteproductpremium = (id: number) =>
  handleRequest(axiosInstance.delete(`${BASE_URL}/deleteproductpremium`, { params: { id } }));

export const changeProductStatus = (id: number, payload: { status: string }) =>
  handleRequest(
    axiosInstance.put(`${BASE_URL}/changeproductstatus`, payload, { params: { id } })
  );


export const getAllProducts = (data: GetProductListParams) =>
  handleRequest(axiosInstance.post(`${BASE_URL}/getallproducts`, data));

export const getAllProductBenefits = (product_id: number) =>
  handleRequest(axiosInstance.get(`${BASE_URL}/getallproductbenfit`, { params: { product_id } }));

export const getProductAdditionalInfo = (product_id: number) =>
  handleRequest(axiosInstance.get(`${BASE_URL}/getallproductaddinfo`, { params: { product_id } }));

export const getAllProductEligibility = (product_id: number) =>
  handleRequest(axiosInstance.get(`${BASE_URL}/getallproducteligibility`, { params: { product_id } }));

export const getAllProductPremium = (product_id: number) =>
  handleRequest(axiosInstance.get(`${BASE_URL}/getallproductpremium`, { params: { product_id } }));
